import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import { brochurePages as brochureMock } from '../data/mock';
import { api } from '../api/client';

export default function BrochureSection() {
  const [pages, setPages] = useState([]);
  const [meta, setMeta] = useState({ title: 'Brochure Ufficiale', subtitle: '', pdf_url: '' });
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [visible, setVisible] = useState(typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : (typeof window !== 'undefined' && window.innerWidth < 1024 ? 2 : 4));

  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      setVisible(w < 768 ? 1 : w < 1024 ? 2 : 4);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let alive = true;
    api.getBrochure().then((d) => {
      if (!alive) return;
      setMeta({ title: d.title || 'Brochure Ufficiale', subtitle: d.subtitle || '', pdf_url: d.pdf_url || '' });
      const list = (d.pages || []).filter((p) => p.src).map((p, i) => ({ id: i + 1, image: p.src, title: p.label || `Pagina ${i + 1}` }));
      setPages(list.length > 0 ? list : brochureMock.map((p) => ({ id: p.id, image: p.image, title: p.title })));
    }).catch(() => {
      setPages(brochureMock.map((p) => ({ id: p.id, image: p.image, title: p.title })));
    });
    return () => { alive = false; };
  }, []);

  if (pages.length === 0) return null;

  const next = () => setIdx((i) => (i + 1) % pages.length);
  const prev = () => setIdx((i) => (i - 1 + pages.length) % pages.length);
  const shown = Array.from({ length: Math.min(visible, pages.length) }, (_, i) => pages[(idx + i) % pages.length]);

  return (
    <section className="py-20 bg-navy-dark">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="section-title centered text-4xl md:text-5xl">{meta.title}</h2>
          {meta.subtitle && <p className="text-white/70 mt-6 max-w-2xl mx-auto">{meta.subtitle}</p>}
          {meta.pdf_url && (
            <a href={meta.pdf_url} target="_blank" rel="noreferrer" className="btn-primary inline-flex mt-5 !py-2 !px-5 !text-sm">
              <Download size={14} /> Scarica brochure PDF
            </a>
          )}
        </div>

        <div className="relative">
          <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-11 h-11 rounded-full bg-pink-brand text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg" aria-label="Precedente">
            <ChevronLeft size={22} />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            {shown.map((p, i) => (
              <button key={`${p.id}-${idx}-${i}`} onClick={() => setLightbox(p)} className="group relative overflow-hidden rounded-xl animate-fade-in bg-navy-dark/40 flex items-center justify-center">
                <img
                  src={p.image}
                  alt={p.title}
                  className="w-full h-auto max-h-[60vh] object-contain group-hover:scale-[1.03] transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/80 via-transparent to-transparent pointer-events-none"></div>
                <span className="absolute bottom-3 left-3 right-3 text-white text-sm font-semibold drop-shadow">{p.title}</span>
              </button>
            ))}
          </div>

          <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-11 h-11 rounded-full bg-pink-brand text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg" aria-label="Successivo">
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-6 flex-wrap">
          {pages.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} className={`h-2 rounded-full transition-all ${i === idx ? 'w-8 bg-pink-brand' : 'w-2 bg-white/30'}`} aria-label={`Vai a pagina ${i + 1}`} />
          ))}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-fade-in" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 w-11 h-11 rounded-full bg-pink-brand text-white flex items-center justify-center" aria-label="Chiudi">
            <X size={22} />
          </button>
          <img src={lightbox.image} alt={lightbox.title} className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </section>
  );
}
